import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Clock, MapPin, BadgeCheck } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { FloatingWhatsApp } from "../components/site/FloatingWhatsApp";
import { Reveal } from "../components/site/Reveal";
import { AREAS, SERVICES, DURATIONS, waLink } from "../data";

const ease = [0.22, 1, 0.36, 1];

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/" replace />;

  return (
    <div data-testid={`area-page-${area.slug}`}>
      <Header />

      <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#2c1e16]">
        <motion.img
          src={area.hero}
          alt={`Massage panggilan area ${area.name}`}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c1e16] via-[#2c1e16]/50 to-[#2c1e16]/30" />
        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pb-16 pt-32">
          <Link
            to="/#area"
            data-testid="area-back-link"
            className="inline-flex items-center gap-2 text-[#e5d9c5] text-sm mb-6 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Semua Area
          </Link>
          <p className="uppercase text-xs tracking-[0.28em] text-[#b8862f] mb-4">
            Massage Panggilan · {area.short}
          </p>
          <h1 className="font-display text-5xl sm:text-7xl text-[#faf7f2] leading-[0.95] tracking-tight">
            Massage Panggilan {area.name}
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-14">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl text-[#2c1e16] leading-tight">
              Massage profesional langsung ke tempat Anda di {area.name}
            </h2>
            <p className="mt-6 text-[#5c4033] leading-relaxed text-lg">
              {area.blurb}
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[#5c4033] text-sm">
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#b8862f]" /> Buka 24 Jam
              </span>
              <span className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-[#b8862f]" /> Terapis Bersertifikat
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#b8862f]" /> Bebas Uang Transport
              </span>
            </div>

            <div className="mt-10">
              <h3 className="uppercase text-xs tracking-[0.2em] text-[#8b5a2b] mb-4">
                Treatment yang tersedia
              </h3>
              <div className="flex flex-wrap gap-3">
                {SERVICES.map((s) => (
                  <span
                    key={s.slug}
                    className="rounded-full border border-[#e5d9c5] px-4 py-1.5 text-sm text-[#5c4033]"
                  >
                    {s.name}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-sm text-[#8b5a2b]">
                Pilihan durasi: {DURATIONS.join(" · ")}
              </p>
            </div>

            <a
              href={waLink(
                `Halo Javanese Spa Jogja, saya di area ${area.name}, ingin booking Massage panggilan.`
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="area-booking-btn"
              className="mt-10 inline-block rounded-full bg-[#5c4033] text-[#faf7f2] px-8 py-4 text-sm hover:bg-[#2c1e16] transition-colors"
            >
              Booking di {area.name} via WhatsApp
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-[#f2ece4] border border-[#e5d9c5] p-8">
              <h3 className="font-display text-2xl text-[#2c1e16]">
                Jangkauan di {area.name}
              </h3>
              <ul className="mt-6 space-y-4">
                {area.landmarks.map((l) => (
                  <li key={l} className="flex items-start gap-3 text-[#5c4033]">
                    <Check className="h-5 w-5 text-[#b8862f] shrink-0 mt-0.5" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-[#e5d9c5]">
                <h4 className="uppercase text-xs tracking-[0.2em] text-[#8b5a2b] mb-3">
                  Area lainnya
                </h4>
                <div className="flex flex-wrap gap-2">
                  {AREAS.filter((a) => a.slug !== area.slug).map((a) => (
                    <Link
                      key={a.slug}
                      to={`/area/${a.slug}`}
                      className="rounded-full bg-[#faf7f2] border border-[#e5d9c5] px-4 py-1.5 text-sm text-[#5c4033] hover:border-[#b8862f]"
                    >
                      {a.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
